<template>
    <div class="cs-input-config">
        <div class="cs-config-section">
            <div class="cs-section-header">
                <span>Input Parameters</span>
                <button class="cs-add-btn" @click="addOption" title="Add parameter">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                </button>
            </div>

            <div v-if="!localOptions.length" class="cs-empty-state">
                <p>No input parameters defined.</p>
                <p class="cs-hint">Add parameters that users can configure when using your indicator.</p>
            </div>

            <!-- Option Definitions -->
            <div
                v-for="(opt, i) in localOptions"
                :key="i"
                class="cs-option-card"
            >
                <div class="cs-option-header">
                    <input
                        class="cs-opt-key"
                        v-model="opt.key"
                        placeholder="key"
                        @input="emitOptions"
                    />
                    <select class="cs-opt-type" v-model="opt.type" @change="onTypeChange(opt)">
                        <option value="number">Number</option>
                        <option value="select">Select</option>
                        <option value="boolean">Boolean</option>
                        <option value="color">Color</option>
                    </select>
                    <button class="cs-opt-remove" @click="removeOption(i)" title="Remove">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div class="cs-option-body">
                    <div class="cs-opt-row">
                        <label>Label</label>
                        <input v-model="opt.label" placeholder="Display label" @input="emitOptions" />
                    </div>

                    <!-- Number type -->
                    <template v-if="opt.type === 'number'">
                        <div class="cs-opt-row cs-opt-row-multi">
                            <div>
                                <label>Default</label>
                                <input type="number" v-model.number="opt.default" @input="emitOptions" />
                            </div>
                            <div>
                                <label>Min</label>
                                <input type="number" v-model.number="opt.min" @input="emitOptions" />
                            </div>
                            <div>
                                <label>Max</label>
                                <input type="number" v-model.number="opt.max" @input="emitOptions" />
                            </div>
                            <div>
                                <label>Step</label>
                                <input type="number" v-model.number="opt.step" placeholder="1" @input="emitOptions" />
                            </div>
                        </div>
                    </template>

                    <!-- Select type -->
                    <template v-if="opt.type === 'select'">
                        <div class="cs-opt-row">
                            <label>Default</label>
                            <select v-model="opt.default" @change="emitOptions">
                                <option v-for="c in (opt.choices || [])" :key="c" :value="c">{{ c }}</option>
                            </select>
                        </div>
                        <div class="cs-opt-row">
                            <label>Choices</label>
                            <input
                                :value="(opt.choices || []).join(', ')"
                                @input="onChoicesInput(opt, $event)"
                                placeholder="open, high, low, close"
                            />
                        </div>
                    </template>

                    <!-- Boolean type -->
                    <template v-if="opt.type === 'boolean'">
                        <div class="cs-opt-row">
                            <label>Default</label>
                            <label class="cs-toggle">
                                <input type="checkbox" v-model="opt.default" @change="emitOptions" />
                                <span class="cs-toggle-slider"></span>
                            </label>
                        </div>
                    </template>

                    <!-- Color type -->
                    <template v-if="opt.type === 'color'">
                        <div class="cs-opt-row">
                            <label>Default</label>
                            <input type="color" v-model="opt.default" @input="emitOptions" />
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- Current Values -->
        <div class="cs-config-section" v-if="localOptions.length">
            <div class="cs-section-header">
                <span>Current Values</span>
                <button class="cs-add-btn" @click="resetValues" title="Reset to defaults">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="1 4 1 10 7 10"/>
                        <path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                    </svg>
                </button>
            </div>

            <div class="cs-values-grid">
                <div v-for="opt in localOptions" :key="opt.key" class="cs-value-row">
                    <label>{{ opt.label || opt.key }}</label>

                    <template v-if="opt.type === 'number'">
                        <input
                            type="number"
                            :value="localValues[opt.key]"
                            :min="opt.min"
                            :max="opt.max"
                            :step="opt.step || 1"
                            @input="onValueChange(opt.key, $event.target.valueAsNumber)"
                        />
                        <input
                            type="range"
                            :value="localValues[opt.key]"
                            :min="opt.min || 1"
                            :max="opt.max || 200"
                            :step="opt.step || 1"
                            @input="onValueChange(opt.key, Number($event.target.value))"
                            class="cs-range"
                        />
                    </template>

                    <template v-if="opt.type === 'select'">
                        <select
                            :value="localValues[opt.key]"
                            @change="onValueChange(opt.key, $event.target.value)"
                        >
                            <option v-for="c in (opt.choices || [])" :key="c" :value="c">{{ c }}</option>
                        </select>
                    </template>

                    <template v-if="opt.type === 'boolean'">
                        <label class="cs-toggle">
                            <input
                                type="checkbox"
                                :checked="localValues[opt.key]"
                                @change="onValueChange(opt.key, $event.target.checked)"
                            />
                            <span class="cs-toggle-slider"></span>
                        </label>
                    </template>

                    <template v-if="opt.type === 'color'">
                        <input
                            type="color"
                            :value="localValues[opt.key]"
                            @input="onValueChange(opt.key, $event.target.value)"
                        />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'InputConfig',
    props: {
        options: { type: Array, default: () => [] },
        values: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            localOptions: JSON.parse(JSON.stringify(this.options)),
            localValues: { ...this.values },
        };
    },
    watch: {
        options: {
            handler(val) {
                this.localOptions = JSON.parse(JSON.stringify(val));
            },
            deep: true,
        },
        values: {
            handler(val) {
                this.localValues = { ...val };
            },
            deep: true,
        },
    },
    methods: {
        addOption() {
            this.localOptions.push({
                key: 'param' + (this.localOptions.length + 1),
                label: 'Parameter ' + (this.localOptions.length + 1),
                type: 'number',
                default: 14,
                min: 1,
                max: 200,
            });
            this.emitOptions();
        },

        removeOption(index) {
            const key = this.localOptions[index].key;
            this.localOptions.splice(index, 1);
            delete this.localValues[key];
            this.emitOptions();
        },

        onTypeChange(opt) {
            if (opt.type === 'number') {
                opt.default = 14;
                opt.min = 1;
                opt.max = 200;
            } else if (opt.type === 'select') {
                opt.choices = ['open', 'high', 'low', 'close'];
                opt.default = 'close';
            } else if (opt.type === 'boolean') {
                opt.default = false;
            } else if (opt.type === 'color') {
                opt.default = '#2962ff';
            }
            this.emitOptions();
        },

        onChoicesInput(opt, event) {
            opt.choices = event.target.value.split(',').map(s => s.trim()).filter(Boolean);
            this.emitOptions();
        },

        emitOptions() {
            this.$emit('update-options', JSON.parse(JSON.stringify(this.localOptions)));
        },

        onValueChange(key, value) {
            this.localValues = { ...this.localValues, [key]: value };
            this.$emit('update-values', { ...this.localValues });
        },

        resetValues() {
            const defaults = {};
            for (const opt of this.localOptions) {
                defaults[opt.key] = opt.default;
            }
            this.localValues = defaults;
            this.$emit('update-values', { ...defaults });
        },
    },
};
</script>

<style scoped>
.cs-input-config {
    padding: 16px;
}

.cs-config-section {
    margin-bottom: 24px;
}

.cs-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--tc-text-secondary, #787b86);
}

.cs-add-btn {
    background: none;
    border: 1px solid var(--tc-border-primary, #e0e3eb);
    border-radius: 6px;
    color: var(--tc-text-secondary, #787b86);
    cursor: pointer;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    transition: all 0.15s;
}
.cs-add-btn:hover {
    border-color: var(--tc-accent, #2962ff);
    color: var(--tc-accent, #2962ff);
}

.cs-empty-state {
    text-align: center;
    padding: 24px;
    color: var(--tc-text-tertiary, #9ca3af);
    font-size: 13px;
}
.cs-hint {
    font-size: 12px;
    margin-top: 4px;
}

.cs-option-card {
    border: 1px solid var(--tc-border-primary, #e0e3eb);
    border-radius: 8px;
    margin-bottom: 12px;
    overflow: hidden;
}

.cs-option-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: var(--tc-bg-secondary, #f8f9fd);
    border-bottom: 1px solid var(--tc-border-secondary, #f0f3fa);
}

.cs-opt-key {
    flex: 1;
    background: var(--tc-input-bg, #fff);
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 13px;
    font-family: 'SF Mono', 'Fira Code', monospace;
    color: var(--tc-text-primary, #131722);
    outline: none;
}

.cs-opt-type {
    background: var(--tc-input-bg, #fff);
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    color: var(--tc-text-primary, #131722);
    outline: none;
    cursor: pointer;
}

.cs-opt-remove {
    background: none;
    border: none;
    color: var(--tc-text-tertiary, #9ca3af);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
}
.cs-opt-remove:hover {
    color: var(--tc-red, #ef4444);
    background: var(--tc-red-bg, rgba(239,68,68,0.1));
}

.cs-option-body {
    padding: 12px;
}

.cs-opt-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}
.cs-opt-row:last-child { margin-bottom: 0; }

.cs-opt-row label {
    font-size: 12px;
    color: var(--tc-text-secondary, #787b86);
    min-width: 60px;
}

.cs-opt-row input,
.cs-opt-row select {
    flex: 1;
    background: var(--tc-input-bg, #fff);
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 13px;
    color: var(--tc-text-primary, #131722);
    outline: none;
}

.cs-opt-row-multi {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}
.cs-opt-row-multi div {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.cs-opt-row-multi label {
    min-width: auto;
}
.cs-opt-row-multi input {
    width: 100%;
}

/* Values section */
.cs-values-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.cs-value-row {
    display: flex;
    align-items: center;
    gap: 10px;
}
.cs-value-row label {
    font-size: 13px;
    color: var(--tc-text-primary, #131722);
    min-width: 100px;
    font-weight: 500;
}
.cs-value-row input[type="number"],
.cs-value-row select {
    width: 80px;
    background: var(--tc-input-bg, #fff);
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 13px;
    color: var(--tc-text-primary, #131722);
    outline: none;
}

.cs-range {
    flex: 1;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: var(--tc-border-primary, #e0e3eb);
    border-radius: 2px;
    outline: none;
}
.cs-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--tc-accent, #2962ff);
    cursor: pointer;
}

/* Toggle */
.cs-toggle {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
    min-width: 36px !important;
}
.cs-toggle input { opacity: 0; width: 0; height: 0; }
.cs-toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background: var(--tc-border-primary, #e0e3eb);
    border-radius: 10px;
    transition: 0.2s;
}
.cs-toggle-slider:before {
    content: "";
    position: absolute;
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background: white;
    border-radius: 50%;
    transition: 0.2s;
}
.cs-toggle input:checked + .cs-toggle-slider {
    background: var(--tc-accent, #2962ff);
}
.cs-toggle input:checked + .cs-toggle-slider:before {
    transform: translateX(16px);
}
</style>
